import contactIcon from '@/assets/icons/contact-icon.png';
import csiLogo from '@/assets/icons/csi-logo-ltgreen.png';
import { useAuth } from '@/auth/useAuth';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Routes } from '../CSIRouter.tsx';
import { ContactBanner } from './ContactBanner';
import { MenuDrawer } from './MenuDrawer.tsx';
import { UserMenu } from './UserMenu';

// Responsive sizing system using viewport units
// Header height: ~56px at 1080px viewport height (5.2vh)
const HEADER_HEIGHT = 'clamp(42px, 5.2vh, 64px)';

// Logo height: ~90% of header height (fills most of the header)
const LOGO_HEIGHT = 'clamp(38px, calc(5.2vh * 0.9), 58px)';

// Profile button height: ~75% of header height
const PROFILE_BUTTON_HEIGHT = 'clamp(32px, calc(5.2vh * 0.75), 48px)';

// Contact button height: ~65% of header height (a bit smaller than profile)
const CONTACT_BUTTON_HEIGHT = 'clamp(27px, calc(5.2vh * 0.65), 42px)';

// Return to Projects button sizing - maintaining aspect ratio, scaled down
const NAV_BUTTON_WIDTH = 'clamp(145px, 8.5vw, 165px)'; // Wider minimum for better text fit
const NAV_BUTTON_HEIGHT = 'clamp(30px, 3.5vh, 38px)'; // Slightly taller
const NAV_BUTTON_FONT = 'clamp(10px, 0.7vw, 12px)';

interface UnifiedHeaderProps {
  hideProfileButton?: boolean;
  contactOpen?: boolean;
  onContactToggle?: () => void;
  showReturnToProjects?: boolean;
  onReturnToProjects?: () => void;
}

export const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  hideProfileButton = false,
  contactOpen,
  onContactToggle,
  showReturnToProjects = false,
  onReturnToProjects,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [internalContactOpen, setInternalContactOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const contactBannerOpen =
    contactOpen !== undefined ? contactOpen : internalContactOpen;
  const handleContactToggle =
    onContactToggle || (() => setInternalContactOpen(!internalContactOpen));

  // Show Return to Projects button on profile page
  const isProfilePage =
    location.pathname === '/profile' ||
    location.pathname === `/${Routes.Root.Profile?.path}`;

  // show Return to Projects button on decarb flow
  const isDecarb = location.pathname.includes('/decarb');

  const isProjectFlow = location.pathname.includes('/project');
  const isProjCreation = location.pathname.includes('/proj-overview');

  return (
    <>
      <AppBar
        position='sticky'
        color='primary'
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: '#0E463F',
          overflow: 'visible',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: `${HEADER_HEIGHT} !important`,
            height: HEADER_HEIGHT,
            px: 0,
            justifyContent: 'space-between',
          }}
        >
          {/* Logo on the left */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component={Link}
              to={'/'}
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                ml: 3,
              }}
            >
              <Box
                component='img'
                src={csiLogo}
                alt='CSI Logo'
                sx={{
                  height: LOGO_HEIGHT,
                }}
              />
            </Box>

            {/* Return to Projects button - only on profile page */}
            {(isProfilePage ||
              isDecarb ||
              isProjectFlow ||
              isProjCreation ||
              showReturnToProjects) && (
              <Button
                {...(onReturnToProjects
                  ? {}
                  : { component: Link, to: '/landing' })}
                onClick={onReturnToProjects}
                disableRipple
                sx={{
                  height: NAV_BUTTON_HEIGHT,
                  borderRadius: '4px',
                  textTransform: 'none',
                  textDecoration: 'none',
                  fontWeight: 600,
                  minWidth: NAV_BUTTON_WIDTH,
                  maxWidth: '200px',
                  fontFamily: '"Inter", sans-serif',
                  color: '#FFFFFF !important',
                  backgroundColor: 'rgba(9, 126, 113, 1)',
                  ml: 'clamp(8px, 1vw, 16px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'rgba(11, 145, 130, 1) !important',
                  },
                  '&:active': {
                    backgroundColor: 'rgba(7, 100, 90, 1) !important',
                  },
                  '& span': {
                    color: '#FFFFFF',
                  },
                }}
              >
                RETURN TO PROJECTS
              </Button>
            )}
          </Box>

          {isDecarb && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontWeight: 800,
                fontSize: NAV_BUTTON_FONT,
                fontFamily: '"Futura PT", sans-serif',
                color: '#7BBCB3',
              }}
            >
              DECARBONIZATION HUB
            </Box>
          )}

          {/* Right side: Contact and Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 6 }}>
            <Tooltip title='Contact'>
              <IconButton
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                onClick={handleContactToggle}
              >
                <Box
                  component='img'
                  src={contactIcon}
                  alt='Contact'
                  sx={{
                    height: CONTACT_BUTTON_HEIGHT,
                    width: 'auto',
                  }}
                />
              </IconButton>
            </Tooltip>
            {user && !hideProfileButton && (
              <UserMenu logoHeight={PROFILE_BUTTON_HEIGHT} />
            )}
          </Box>
        </Toolbar>

        {/* Contact Banner - positioned absolutely relative to AppBar */}
        <ContactBanner
          open={contactBannerOpen}
          onClose={() => {
            if (onContactToggle) {
              onContactToggle();
            } else {
              setInternalContactOpen(false);
            }
          }}
          userEmail={user?.email || ''}
        />
      </AppBar>

      {/* Side Menu - kept for navigation access */}
      <MenuDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        isMobile={isMobile}
      />
    </>
  );
};
